import type { KbCategory, KbDocDetailDTO } from '@copilot/shared';
import { KbCategorySchema, KbDocBodySchema } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { kbApi } from '../../api/admin';
import type { KbDocPatch } from '../../api/types';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  ErrorBanner,
  Field,
  Input,
  PageSkeleton,
  PageHeader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '../../components';
import { formatDateTime, formatNumber } from '../../lib/format';
import { KB_CATEGORY_LABELS } from '../../lib/labels';

interface DocForm {
  title: string;
  category: KbCategory;
  content: string;
  active: boolean;
}

/** /kb/new creates a document; /kb/:id edits one (new content = new version). */
export function KbDocPage() {
  const { id } = useParams();
  const query = useQuery({ queryKey: ['kb', 'doc', id], queryFn: () => kbApi.get(id!), enabled: !!id });

  if (!id) return <CreateDoc />;
  if (query.isLoading) return <PageSkeleton />;
  if (query.isError)
    return (
      <div className="space-y-3">
        <BackLink />
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  return query.data ? <EditDoc key={`${query.data.id}-${query.data.version}`} doc={query.data} /> : null;
}

function BackLink() {
  return (
    <Link to="/kb" className="text-xs text-muted hover:text-ink">
      ← Back to knowledge base
    </Link>
  );
}

function DocFields({ form, setForm, errors }: { form: DocForm; setForm: (f: DocForm) => void; errors: Record<string, string> }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_16rem]">
        <Field label="Title" error={errors.title}>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} />
        </Field>
        <Field label="Category">
          <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as KbCategory })}>
            {KbCategorySchema.options.map((c) => (
              <option key={c} value={c}>
                {KB_CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Content (Markdown)" error={errors.content} hint={`${formatNumber(form.content.length)} characters`}>
        <Textarea
          rows={22}
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          className="font-mono text-[13px] leading-relaxed"
          spellCheck
        />
      </Field>
      <Checkbox
        checked={form.active}
        onChange={(e) => setForm({ ...form, active: e.target.checked })}
        label="Active (used by the AI during calls)"
      />
    </div>
  );
}

function validate(form: DocForm): Record<string, string> {
  const parsed = KbDocBodySchema.safeParse(form);
  const errors: Record<string, string> = {};
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0] ?? '');
      errors[key] ??= key === 'title' ? 'Title is required (max 200 characters).' : key === 'content' ? 'Content is required.' : issue.message;
    }
  }
  return errors;
}

function CreateDoc() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DocForm>({ title: '', category: 'service', content: '', active: true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () => kbApi.create({ ...form, title: form.title.trim() }),
    onSuccess: (doc) => {
      void queryClient.invalidateQueries({ queryKey: ['kb'] });
      void navigate(`/kb/${doc.id}`, { replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs = validate({ ...form, title: form.title.trim() });
    setErrors(errs);
    if (Object.keys(errs).length === 0) create.mutate();
  };
  return (
    <div className="space-y-4">
      <BackLink />
      <PageHeader title="New document" />
      <form onSubmit={submit}>
        <Card>
          <DocFields form={form} setForm={setForm} errors={errors} />
          <ErrorBanner error={create.error} className="mt-3" />
          <div className="mt-4 flex justify-end gap-2">
            <Button type="submit" variant="primary" loading={create.isPending}>
              Create document
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}

function EditDoc({ doc }: { doc: KbDocDetailDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial: DocForm = { title: doc.title, category: doc.category, content: doc.content, active: doc.active };
  const [form, setForm] = useState<DocForm>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['kb'] });

  const patch: KbDocPatch = {};
  if (form.title.trim() !== doc.title) patch.title = form.title.trim();
  if (form.category !== doc.category) patch.category = form.category;
  if (form.active !== doc.active) patch.active = form.active;
  if (form.content !== doc.content) patch.content = form.content;
  const dirty = Object.keys(patch).length > 0;

  const save = useMutation({ mutationFn: () => kbApi.patch(doc.id, patch), onSuccess: invalidate });
  const restore = useMutation({
    mutationFn: (version: number) => kbApi.restore(doc.id, version),
    onSuccess: () => {
      setRestoreVersion(null);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => kbApi.remove(doc.id),
    onSuccess: () => {
      invalidate();
      void navigate('/kb', { replace: true });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs = validate({ ...form, title: form.title.trim() });
    setErrors(errs);
    if (Object.keys(errs).length === 0 && dirty) save.mutate();
  };

  const versions = [...doc.versions].sort((a, b) => b.version - a.version);

  return (
    <div className="space-y-4">
      <BackLink />
      <PageHeader
        title={doc.title}
        description={
          <>
            {KB_CATEGORY_LABELS[doc.category]} · version {doc.version} · {formatNumber(doc.tokens)} tokens
            {doc.sourceFilename && ` · from ${doc.sourceFilename}`}
          </>
        }
        actions={
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        }
      />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <form onSubmit={submit}>
          <Card>
            <DocFields form={form} setForm={setForm} errors={errors} />
            <ErrorBanner error={save.error} className="mt-3" />
            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              {patch.content !== undefined && <span className="text-xs text-muted">Saving creates version {doc.version + 1}.</span>}
              {dirty && (
                <Button onClick={() => setForm(initial)} variant="ghost">
                  Discard changes
                </Button>
              )}
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
                Save
              </Button>
            </div>
          </Card>
        </form>
        <Card title="Version history" bodyClassName="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Version</Th>
                <Th>Created</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <Tr key={v.version}>
                  <Td className="whitespace-nowrap">
                    v{v.version} {v.version === doc.version && <Badge tone="accent">Current</Badge>}
                    <div className="text-xs text-muted">{formatNumber(v.tokens)} tokens</div>
                  </Td>
                  <Td className="text-xs text-ink-2">
                    {formatDateTime(v.createdAt)}
                    {v.createdByName && <div className="text-muted">{v.createdByName}</div>}
                  </Td>
                  <Td className="text-right">
                    {v.version !== doc.version && (
                      <Button size="sm" variant="ghost" onClick={() => setRestoreVersion(v.version)}>
                        Restore
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>

      <ConfirmDialog
        open={restoreVersion != null}
        title={`Restore version ${restoreVersion}`}
        confirmLabel="Restore"
        loading={restore.isPending}
        error={restore.error}
        onConfirm={() => restoreVersion != null && restore.mutate(restoreVersion)}
        onCancel={() => {
          setRestoreVersion(null);
          restore.reset();
        }}
      >
        This creates version {doc.version + 1} with the content of version {restoreVersion}.
        {dirty && ' Your unsaved changes will be lost.'}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete document"
        danger
        confirmLabel="Delete"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutate()}
        onCancel={() => {
          setConfirmDelete(false);
          remove.reset();
        }}
      >
        Delete <strong>{doc.title}</strong>? The AI will stop using it immediately.
      </ConfirmDialog>
    </div>
  );
}
